import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { SEO } from "@/components/SEO";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { FAQSection } from "@/components/FAQSection";
import { CTABand } from "@/components/CTABand";
import { FAQS, IMAGES } from "@/content/site";

const INCLUDED = [
  "Complete water testing and precise chemical balancing every visit",
  "Skimming the surface and emptying skimmer and pump baskets",
  "Brushing walls, steps, benches, and the waterline",
  "Vacuuming the pool floor as needed",
  "Routine filter checks and scheduled filter cleaning",
  "Salt cell inspection and care for saltwater pools",
  "Visual equipment inspection to catch issues early",
  "Clear communication about your pool after every visit",
];

const CHEMISTRY = [
  { label: "pH", target: "7.4 – 7.6" },
  { label: "Free Chlorine", target: "1 – 3 ppm" },
  { label: "Total Alkalinity", target: "80 – 120 ppm" },
  { label: "Cyanuric Acid", target: "30 – 50 ppm" },
  { label: "Calcium Hardness", target: "200 – 400 ppm" },
];

export default function PoolServicePage() {
  return (
    <>
      <SEO
        title="Weekly Pool Service & Cleaning in Pinellas County FL | Point of Pines Pools"
        description="Reliable weekly pool cleaning and maintenance across Pinellas County, FL — Clearwater, Largo, St. Petersburg, Seminole and more. Professional water chemistry from a CPO certified local team. Call (727) 744-5908."
        path="/pool-service/"
      />
      <PageHero
        overline="Pool Service — Pinellas County, FL"
        title="Weekly Pool Service"
        accent="Done Right, Every Week"
        description="Dependable residential pool cleaning and maintenance across Pinellas County. Consistent service days, professional water chemistry, and communication you can count on."
        image={IMAGES.servicePool}
        imageAlt="Crystal clear blue backyard pool after weekly pool service by Point of Pines Pools in Pinellas County"
      />

      <section data-testid="service-included-section" className="py-16 sm:py-24 lg:py-32 bg-shell">
        <div className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <Reveal>
              <p className="text-xs font-mono uppercase tracking-[0.25em] text-pool font-semibold">What&apos;s Included</p>
              <h2 className="mt-4 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight leading-[1.15] text-navy">
                One visit. Every detail handled.
              </h2>
              <p className="mt-5 text-base text-slate-600 leading-relaxed">
                Weekly pool service shouldn&apos;t be a mystery. Here&apos;s what a typical Point of
                Pines visit covers — whether your pool is in Clearwater, Seminole, St. Pete, or
                anywhere else in Pinellas County.
              </p>
            </Reveal>
            <Reveal delay={0.12} className="mt-8">
              <div className="clip-arch overflow-hidden ring-1 ring-slate-200 shadow-xl shadow-navy/10">
                <img
                  src={IMAGES.mikeNet}
                  alt="Owner Mike Strawn skimming the surface of a residential pool during weekly service in Pinellas County FL"
                  className="w-full h-96 sm:h-[28rem] object-cover object-[50%_68%]"
                  loading="lazy"
                  data-testid="service-included-photo"
                />
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <ul className="grid sm:grid-cols-2 gap-4">
              {INCLUDED.map((item, i) => (
                <motion.li
                  key={item}
                  data-testid={`included-item-${i}`}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ duration: 0.5, delay: (i % 2) * 0.08 }}
                  className="flex items-start gap-3.5 bg-white rounded-xl border border-slate-200/80 p-5"
                >
                  <span className="w-7 h-7 rounded-full bg-mist flex items-center justify-center shrink-0 mt-0.5">
                    <Check className="w-4 h-4 text-pool" />
                  </span>
                  <span className="text-sm sm:text-[15px] text-slate-700 leading-relaxed">{item}</span>
                </motion.li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section data-testid="chemistry-section" className="py-16 sm:py-24 bg-navy text-white relative overflow-hidden">
        <div className="noise-overlay absolute inset-0 opacity-[0.04] pointer-events-none" />
        <div className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto relative">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
            <Reveal className="lg:col-span-6">
              <p className="text-xs font-mono uppercase tracking-[0.25em] text-sky2 font-semibold">The Science Part</p>
              <h2 className="mt-4 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight leading-[1.15]">
                Water chemistry we actually manage
              </h2>
              <p className="mt-5 text-base text-slate-300 leading-relaxed">
                As a CPO certified company, we test and balance against professional target ranges —
                not a quick dip-strip guess. Here&apos;s what we watch every week.
              </p>
            </Reveal>
            <Reveal delay={0.12} className="lg:col-span-6">
              <div className="clip-arch overflow-hidden ring-1 ring-white/15 shadow-2xl shadow-black/30">
                <img
                  src={IMAGES.waterTestHand}
                  alt="Testing pool water chemistry with a professional comparator kit over crystal clear water in Pinellas County"
                  className="w-full h-72 sm:h-96 object-cover"
                  loading="lazy"
                  data-testid="chemistry-photo"
                />
              </div>
            </Reveal>
          </div>
          <div className="mt-12 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            {CHEMISTRY.map((c, i) => (
              <motion.div
                key={c.label}
                data-testid={`chemistry-card-${i}`}
                initial={{ opacity: 0, y: 22 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.55, delay: i * 0.07 }}
                className="bg-white/[0.06] backdrop-blur-sm ring-1 ring-white/15 rounded-2xl p-5 sm:p-6 text-center transition-colors duration-300 hover:bg-white/10"
              >
                <p className="text-[11px] font-mono uppercase tracking-[0.18em] text-sky2">{c.label}</p>
                <p className="mt-3 font-display text-xl sm:text-2xl font-bold tracking-tight">{c.target}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <FAQSection faqs={FAQS} />
      <CTABand
        title="Ready for dependable weekly pool service?"
        text="Tell us about your pool and we'll follow up with a clear, no-pressure quote for weekly care."
      />
    </>
  );
}
